import { Command as CommandPrimitive } from 'cmdk';
import { Search } from 'lucide-react';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type HTMLAttributes } from 'react';
import { cn } from '../lib/cn';
import { Dialog, DialogContent, DialogTitle } from './dialog';

export const Command = forwardRef<
  ElementRef<typeof CommandPrimitive>,
  ComponentPropsWithoutRef<typeof CommandPrimitive>
>(({ className, ...props }, ref) => (
  <CommandPrimitive
    ref={ref}
    className={cn(
      'flex h-full w-full flex-col overflow-hidden rounded-lg bg-bg-surface text-text-primary',
      className,
    )}
    {...props}
  />
));
Command.displayName = 'Command';

export interface CommandDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Forwarded to cmdk — pass false when results are filtered server-side. */
  shouldFilter?: boolean;
  /** Controlled selection (cmdk item value) — pair with onValueChange. */
  value?: string;
  onValueChange?: (value: string) => void;
  /** Remounts the cmdk root when it changes — resets internal selection to
   *  the first item. Use for server-driven result sets where forcing a
   *  controlled value races cmdk's own mount/unmount selection logic. */
  commandKey?: string;
  children?: React.ReactNode;
}

export function CommandDialog({ open, onOpenChange, shouldFilter, value, onValueChange, commandKey, children }: CommandDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" className="overflow-hidden p-0" showClose={false}>
        <DialogTitle className="sr-only">Command palette</DialogTitle>
        <Command
          key={commandKey}
          shouldFilter={shouldFilter}
          value={value}
          onValueChange={onValueChange}
          className="[&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:text-label [&_[cmdk-group-heading]]:text-text-muted"
        >
          {children}
        </Command>
      </DialogContent>
    </Dialog>
  );
}

export const CommandInput = forwardRef<
  ElementRef<typeof CommandPrimitive.Input>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Input>
>(({ className, ...props }, ref) => (
  <div className="flex h-12 items-center gap-2.5 border-b border-border px-3.5 focus-within:border-text-muted" cmdk-input-wrapper="">
    <Search className="h-4 w-4 shrink-0 text-text-muted" aria-hidden />
    <CommandPrimitive.Input
      ref={ref}
      className={cn(
        'min-w-0 flex h-full w-full bg-transparent py-2 text-body text-text-primary outline-none placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-0',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    />
  </div>
));
CommandInput.displayName = 'CommandInput';

export const CommandList = forwardRef<
  ElementRef<typeof CommandPrimitive.List>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.List>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.List
    ref={ref}
    className={cn('max-h-[420px] overflow-y-auto overflow-x-hidden p-2', className)}
    {...props}
  />
));
CommandList.displayName = 'CommandList';

export const CommandEmpty = forwardRef<
  ElementRef<typeof CommandPrimitive.Empty>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Empty>
>((props, ref) => (
  <CommandPrimitive.Empty
    ref={ref}
    className="py-6 text-center text-body text-text-muted"
    {...props}
  />
));
CommandEmpty.displayName = 'CommandEmpty';

export const CommandGroup = forwardRef<
  ElementRef<typeof CommandPrimitive.Group>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Group>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Group
    ref={ref}
    className={cn(
      'overflow-hidden p-1 text-text-primary [&_[cmdk-group-heading]]:px-3 [&_[cmdk-group-heading]]:py-2',
      '[&_[cmdk-group-heading]]:text-label [&_[cmdk-group-heading]]:text-text-muted',
      className,
    )}
    {...props}
  />
));
CommandGroup.displayName = 'CommandGroup';

export const CommandSeparator = forwardRef<
  ElementRef<typeof CommandPrimitive.Separator>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Separator
    ref={ref}
    className={cn('-mx-1 h-px bg-border', className)}
    {...props}
  />
));
CommandSeparator.displayName = 'CommandSeparator';

export const CommandItem = forwardRef<
  ElementRef<typeof CommandPrimitive.Item>,
  ComponentPropsWithoutRef<typeof CommandPrimitive.Item>
>(({ className, ...props }, ref) => (
  <CommandPrimitive.Item
    ref={ref}
    className={cn(
      'relative flex cursor-default select-none items-center gap-3 rounded-sm px-3 h-11 text-body outline-none',
      'aria-selected:bg-bg-inset aria-selected:text-text-primary',
      'data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-50',
      className,
    )}
    {...props}
  />
));
CommandItem.displayName = 'CommandItem';

export const CommandShortcut = ({ className, ...props }: HTMLAttributes<HTMLSpanElement>) => (
  <span
    className={cn('ml-auto text-caption tracking-widest text-text-muted', className)}
    {...props}
  />
);
